import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ChevronRight, Activity, Sun, PersonStanding, HeartPulse, CheckCircle2, AlertCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewWidget from "@/components/ReviewWidget";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";

export default function WhatWeTreat() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const getBadgeVariant = (therapy: string) => {
    if (therapy.includes("DRX9000")) return "default";
    if (therapy.includes("SoftWave")) return "secondary";
    if (therapy.includes("Red Light")) return "destructive";
    if (therapy.includes("ChiroThin")) return "outline";
    return "outline";
  };

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="What We Treat | Woods Spine & Wellness | Longview TX"
        description="Discover the conditions we treat at Woods Spine & Wellness in Longview, TX, including sciatica, neuropathy, plantar fasciitis, and herniated discs."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/0e672985-fa6e-4138-9dbb-7de30c83f267.png"
        canonical="https://woodsspine.com/what-we-treat"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden bg-primary">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/0e672985-fa6e-4138-9dbb-7de30c83f267.png&w=1920&output=webp&q=80" 
              alt="Healthy active person walking outdoors" 
              className="w-full h-full object-cover object-center opacity-40"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          </div>
          
          <div className="container relative z-10 mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 [text-shadow:_0_1px_3px_rgba(0,0,0,0.5)]">
                What We Treat
              </h1>
              <p className="text-lg md:text-xl mb-8 text-white/90 [text-shadow:_0_1px_2px_rgba(0,0,0,0.5)]">
                Comprehensive care for a pain-free life. We utilize advanced, non-invasive therapies like SoftWave TRT, Red Light Therapy, and DRX9000 Decompression to target the root cause of your pain.
              </p>
              <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
                <Link to="/appointment-scheduling">
                  Schedule a Consultation <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Introduction / Philosophy */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-primary mb-6">A Modern Approach to Healing</h2>
              <p className="text-lg text-muted-foreground mb-6">
                At Woods Spine & Wellness, we don't just mask your symptoms with medication. We diagnose the underlying cause of your discomfort and apply the most effective, state-of-the-art therapies to promote natural healing and tissue regeneration.
              </p>
              <p className="text-lg text-muted-foreground">
                Whether you're suffering from a recent sports injury, chronic joint pain, or debilitating spinal issues, our tailored treatment plans are designed to help you recover faster and get back to doing what you love. Below is detailed information on the primary conditions we treat.
              </p>
            </div>
          </div>
        </section>

        {/* DETAILED CONDITION: HERNIATED DISCS */}
        <section id="herniated-discs" className="py-16 border-t border-border">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <PersonStanding className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary">Herniated & Bulging Discs</h2>
            </div>
            
            <div className="prose prose-lg text-muted-foreground mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">What It Is</h3>
              <p>
                A herniated disc (often called a slipped or ruptured disc) occurs when the soft, jelly-like center of a spinal disc pushes through a tear in its tougher exterior casing. A bulging disc is similar but occurs when the disc simply protrudes outward without the inner material leaking out. Both conditions can compress nearby spinal nerves, causing severe localized back or neck pain, as well as pain, numbness, or weakness that radiates down the arms or legs.
              </p>
              
              <h3 className="text-2xl font-bold text-foreground mb-4 mt-8">How We Treat It</h3>
              <p>
                Our primary treatment for herniated and bulging discs is DRX9000® Spinal Decompression. This advanced technology gently stretches the spine, creating a vacuum (negative pressure) inside the affected disc. This vacuum effect draws the herniated or bulging material back into its proper place, taking pressure off the pinched nerve. Simultaneously, it pulls nutrient-rich fluids back into the disc to promote natural healing and rehydration. We may also utilize SoftWave TRT to reduce surrounding tissue inflammation and accelerate cellular repair.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-accent mr-2" /> Who It's For
                </h4>
                <p className="text-muted-foreground">Ideal for patients experiencing chronic back pain, radiating leg/arm pain, or numbness caused by diagnosed disc issues who want to avoid spinal fusion or discectomy surgery.</p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 text-destructive mr-2" /> Contraindications
                </h4>
                <p className="text-muted-foreground">Not suitable for pregnant women, patients with severe osteoporosis, or those who have had prior spinal fusion surgery with retained hardware in the affected area.</p>
              </div>
            </div>

            <div className="bg-secondary/30 p-6 rounded-xl border border-border mb-8">
              <h4 className="text-xl font-bold text-primary mb-4">What to Expect</h4>
              <p className="text-muted-foreground">A typical decompression session lasts 30-45 minutes and is completely painless; many patients fall asleep. A full protocol generally requires 20-24 sessions over 6-8 weeks. There is no downtime, but heavy lifting should be avoided during the treatment period.</p>
            </div>

            <div className="space-y-6">
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">Is decompression covered by insurance?</h4>
                <p className="text-muted-foreground">Computerized spinal decompression is typically not covered by major medical insurance. However, we offer flexible financing options and payment plans to make this care accessible.</p>
              </div>
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">How long until I see results for my herniated disc?</h4>
                <p className="text-muted-foreground">Many patients feel relief within the first few weeks of treatment, but it is critical to complete the full 6-8 week protocol to ensure the disc fully heals and rehydrates.</p>
              </div>
              <div className="pb-2">
                <h4 className="text-lg font-bold text-primary mb-2">Is the treatment safe?</h4>
                <p className="text-muted-foreground">Yes, DRX9000 decompression is FDA-cleared and highly safe. It uses computerized sensors to monitor muscle tension, preventing spasms and ensuring a comfortable stretch.</p>
              </div>
            </div>
          </div>
        </section>

        {/* DETAILED CONDITION: SCIATICA */}
        <section id="sciatica" className="py-16 bg-secondary/30 border-t border-border">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <Activity className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary">Sciatica & Leg Pain</h2>
            </div>
            
            <div className="prose prose-lg text-muted-foreground mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">What It Is</h3>
              <p>
                Sciatica refers to pain that radiates along the path of the sciatic nerve, which branches from your lower back through your hips and buttocks and down each leg. It is typically caused by a herniated disc, bone spur, or spinal stenosis compressing part of the nerve. This compression causes inflammation, pain, and often some numbness or tingling in the affected leg. The pain can vary widely, from a mild ache to a sharp, burning sensation or excruciating discomfort.
              </p>
              
              <h3 className="text-2xl font-bold text-foreground mb-4 mt-8">How We Treat It</h3>
              <p>
                Treating sciatica requires addressing the root cause of the nerve compression. Our primary approach utilizes DRX9000 Spinal Decompression to gently separate the lumbar vertebrae, creating space and relieving the pressure on the sciatic nerve root. We also frequently incorporate SoftWave TRT to rapidly decrease the intense inflammation around the nerve and stimulate cellular repair, providing faster symptomatic relief while the decompression heals the underlying disc issue.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-accent mr-2" /> Who It's For
                </h4>
                <p className="text-muted-foreground">Patients suffering from shooting leg pain, lower back pain, or numbness/tingling in the legs and feet caused by nerve compression or piriformis syndrome.</p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 text-destructive mr-2" /> Contraindications
                </h4>
                <p className="text-muted-foreground">Not recommended for patients with active spinal infections, severe osteoporosis, or those who are pregnant. A thorough exam is required to determine the exact cause of the sciatic pain.</p>
              </div>
            </div>

            <div className="bg-background p-6 rounded-xl border border-border mb-8 shadow-sm">
              <h4 className="text-xl font-bold text-primary mb-4">What to Expect</h4>
              <p className="text-muted-foreground">Treatments are non-invasive and generally relaxing. Depending on whether we use SoftWave, Decompression, or both, sessions last 15 to 45 minutes. Most patients require a customized protocol of sessions over several weeks to fully resolve the nerve compression and inflammation. There is no recovery downtime.</p>
            </div>

            <div className="space-y-6">
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">Can sciatica be cured without surgery?</h4>
                <p className="text-muted-foreground">Yes. The vast majority of sciatica cases can be successfully treated non-surgically by relieving the pressure on the nerve through spinal decompression and reducing inflammation.</p>
              </div>
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">How long does it take for sciatic pain to go away?</h4>
                <p className="text-muted-foreground">With advanced therapies like SoftWave and DRX9000, many patients notice a reduction in sharp pain within the first few sessions, though complete healing of the nerve and disc takes several weeks.</p>
              </div>
              <div className="pb-2">
                <h4 className="text-lg font-bold text-primary mb-2">Is the treatment safe?</h4>
                <p className="text-muted-foreground">Absolutely. Both SoftWave TRT and DRX9000 are FDA-cleared, non-invasive therapies with excellent safety profiles and no significant side effects.</p>
              </div>
            </div>
          </div>
        </section>

        {/* DETAILED CONDITION: NEUROPATHY */}
        <section id="neuropathy" className="py-16 border-t border-border">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <Sun className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary">Peripheral Neuropathy</h2>
            </div>
            
            <div className="prose prose-lg text-muted-foreground mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">What It Is</h3>
              <p>
                Peripheral neuropathy is a condition resulting from damage to the peripheral nerves, often causing weakness, numbness, and pain, usually in the hands and feet. Patients frequently describe the pain as stabbing, burning, or tingling. It can be caused by traumatic injuries, infections, metabolic problems, inherited causes, and most commonly, diabetes. Traditional medicine often relies on medications like Gabapentin to mask the symptoms, which can come with heavy side effects.
              </p>
              
              <h3 className="text-2xl font-bold text-foreground mb-4 mt-8">How We Treat It</h3>
              <p>
                Our goal is to stimulate nerve healing and restore proper blood flow to the extremities. We primarily use SoftWave TRT and Red Light Therapy for neuropathy. SoftWave's acoustic waves promote angiogenesis (the creation of new blood vessels), which dramatically increases blood flow to the damaged nerves, providing them with the oxygen and nutrients needed to repair. It also recruits stem cells to aid in tissue regeneration. Red Light Therapy further boosts cellular energy (ATP) production, reducing oxidative stress and accelerating the nerve healing process.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-accent mr-2" /> Who It's For
                </h4>
                <p className="text-muted-foreground">Ideal for patients suffering from diabetic neuropathy, chemotherapy-induced neuropathy, or idiopathic neuropathy who are experiencing numbness, burning, or tingling in their hands or feet.</p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 text-destructive mr-2" /> Contraindications
                </h4>
                <p className="text-muted-foreground">Not suitable for patients with active cancer in the treatment area, those with pacemakers (for SoftWave), or pregnant women. A full consultation is required.</p>
              </div>
            </div>

            <div className="bg-secondary/30 p-6 rounded-xl border border-border mb-8">
              <h4 className="text-xl font-bold text-primary mb-4">What to Expect</h4>
              <p className="text-muted-foreground">Sessions are quick (15-30 minutes) and painless. During SoftWave, you will feel a mild pulsing sensation. During Red Light Therapy, you will feel a soothing warmth. A typical neuropathy protocol involves 2-3 sessions per week for several weeks. There is no downtime, and you can resume normal activities immediately.</p>
            </div>

            <div className="space-y-6">
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">Can nerve damage really be reversed?</h4>
                <p className="text-muted-foreground">While severe, long-term nerve death cannot always be reversed, many patients experience significant regeneration and symptom relief when blood flow is restored and stem cells are activated through our therapies.</p>
              </div>
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">Is this covered by insurance?</h4>
                <p className="text-muted-foreground">These advanced regenerative therapies are generally considered elective and are not covered by insurance. We offer affordable packages and financing to help you get the care you need.</p>
              </div>
              <div className="pb-2">
                <h4 className="text-lg font-bold text-primary mb-2">How soon will the numbness go away?</h4>
                <p className="text-muted-foreground">Nerve tissue heals slowly. While pain and burning often decrease within the first few weeks, improvements in numbness and sensation typically take longer, often continuing to improve for months after treatment ends.</p>
              </div>
            </div>
          </div>
        </section>

        {/* DETAILED CONDITION: PLANTAR FASCIITIS */}
        <section id="plantar-fasciitis" className="py-16 bg-secondary/30 border-t border-border">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <HeartPulse className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary">Plantar Fasciitis</h2>
            </div>
            
            <div className="prose prose-lg text-muted-foreground mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">What It Is</h3>
              <p>
                Plantar fasciitis is one of the most common causes of heel pain. It involves inflammation of a thick band of tissue (the plantar fascia) that runs across the bottom of your foot and connects your heel bone to your toes. The pain is usually worst with the first few steps in the morning or after long periods of standing. It is common in runners, overweight individuals, and those who wear shoes with inadequate support.
              </p>
              
              <h3 className="text-2xl font-bold text-foreground mb-4 mt-8">How We Treat It</h3>
              <p>
                SoftWave TRT is exceptionally effective for treating plantar fasciitis and heel spurs. The electrohydraulic shockwaves penetrate deep into the inflamed plantar fascia, breaking up scar tissue and stimulating the body's natural healing cascade. The acoustic waves increase blood flow to the poorly vascularized fascia tissue and recruit stem cells to repair the micro-tears. This provides rapid pain relief and long-term tissue regeneration without the need for painful cortisone injections or invasive foot surgery.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-accent mr-2" /> Who It's For
                </h4>
                <p className="text-muted-foreground">Ideal for anyone suffering from chronic heel pain, morning foot stiffness, or diagnosed plantar fasciitis who wants to avoid steroid injections or surgery.</p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-primary mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 text-destructive mr-2" /> Contraindications
                </h4>
                <p className="text-muted-foreground">SoftWave is very safe for the foot, but is not used over active infections, tumors, or in patients with severe blood clotting disorders.</p>
              </div>
            </div>

            <div className="bg-background p-6 rounded-xl border border-border mb-8 shadow-sm">
              <h4 className="text-xl font-bold text-primary mb-4">What to Expect</h4>
              <p className="text-muted-foreground">Treatments take only 10-15 minutes per foot. Gel is applied, and the SoftWave applicator is moved over the heel and arch. You will feel a pulsing sensation and a mild "zing" over the most inflamed areas. Most patients require 6-8 sessions. There is no downtime, though we recommend avoiding high-impact running during the initial healing phase.</p>
            </div>

            <div className="space-y-6">
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">Is SoftWave better than cortisone shots?</h4>
                <p className="text-muted-foreground">Cortisone shots only mask the pain temporarily and can actually weaken the tissue over time. SoftWave stimulates actual tissue regeneration and healing for a long-term solution.</p>
              </div>
              <div className="border-b border-border pb-4">
                <h4 className="text-lg font-bold text-primary mb-2">How long until I can walk without pain?</h4>
                <p className="text-muted-foreground">Many patients experience a significant reduction in pain after their very first SoftWave session, particularly noticing less stiffness the following morning.</p>
              </div>
              <div className="pb-2">
                <h4 className="text-lg font-bold text-primary mb-2">Will this treat my heel spur too?</h4>
                <p className="text-muted-foreground">Yes. While the bone spur itself may remain, SoftWave effectively treats the inflammation and tissue damage surrounding the spur, which is the actual source of the pain.</p>
              </div>
            </div>
          </div>
        </section>

        {/* LOCAL RELEVANCE */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Dedicated to the Health of Longview, TX</h2>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              At Woods Spine & Wellness, we are deeply committed to bringing the most advanced, non-invasive healthcare technologies to our local community in Longview, TX, and the greater East Texas region. We believe our neighbors deserve access to world-class regenerative therapies without having to travel to major metropolitan areas. Whether you are dealing with a herniated disc, chronic neuropathy, or stubborn plantar fasciitis, our dedicated team is here to help you get back to a better you, right here at home.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-secondary/30 text-center border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Don't Let Pain Control Your Life</h2>
              <p className="text-xl text-muted-foreground mb-10">
                Contact us today to find out which of our advanced therapies is right for your specific condition. We'll build a personalized treatment plan to get you back to a better you.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
                  <Link to="/appointment-scheduling">
                    Book an Appointment <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="bg-transparent border-primary text-primary hover:bg-primary/10" asChild>
                  <a href="tel:+19032158828">Call (903) 215-8828</a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
